import { Dispatch } from 'redux';
import moment from 'moment';
import { getFormValues } from 'redux-form';

import { userInfoService } from 'app/api/service/user-info-service';
import { NavigationService, navigationService } from 'app/service/navigation-service';
import { RootState } from 'app/redux/state/root-state';
import { CustomerService } from 'app/page/orders/service/customer-service';
import { ShoppingCartService } from 'app/page/orders/service/shopping-cart-service';
import { AvailableTimeProvider } from 'app/page/orders/reschedule-technician/available-time-provider';
import { ActiveOrder } from 'app/page/orders/model/active-order';
import { OrderUtils } from 'app/page/orders/utils/order-utils';
import { BillingAccount } from 'app/page/orders/model/billing-account';
import { BillingAccountUtils } from 'app/page/orders/utils/billing-account-utils';
import { BillingAccountMapper } from 'app/page/orders/mapper/billing-account-mapper';
import { FormName } from 'app/page/orders/form-name';
import { UpdateActivationDateFormModel } from 'app/page/orders/update-activation-date/update-activation-date-form-model';
import { openSuccessNotification } from 'app/components/domain/notification';
import { i18n } from 'app/i18n';
import { PartnerStaticData } from 'app/page/orders/static/partner-static-data';

import { InitializeScheduleAppointmentStartAction } from './model/actions/initialize-schedule-appointment-start-action';
import { InitializeScheduleAppointmentSuccessAction } from './model/actions/initialize-schedule-appointment-success-action';
import { InitializeScheduleAppointmentFailureAction } from './model/actions/initialize-schedule-appointment-failure-action';
import { FetchAvailableTimeStartAction } from './model/actions/fetch-available-time-start-action';
import { FetchAvailableTimeSuccessAction } from './model/actions/fetch-available-time-success-action';
import { LoadBillingAccountsStartAction } from './model/actions/load-billing-accounts-start-action';
import { LoadBillingAccountsSuccessAction } from './model/actions/load-billing-accounts-success-action';
import { LoadBillingAccountsFailureAction } from './model/actions/load-billing-accounts-failure-action';
import { CreateOrderSummaryStartAction } from './model/actions/create-order-summary-start-action';
import { CreateOrderSummarySuccessAction } from './model/actions/create-order-summary-success-action';
import { CreateOrderSummaryFailureAction } from './model/actions/create-order-summary-failure-action';
import { LoadInflightOrdersStartAction } from './model/actions/load-inflight-orders-start-action';
import { LoadInflightOrdersSuccessAction } from './model/actions/load-inflight-orders-success-action';
import { LoadInflightOrdersFailureAction } from './model/actions/load-inflight-orders-failure-action';
import { SetSelectedBillingAccountIdAction } from './model/actions/set-selected-billing-account-id-action';
import { SetSelectedOrderIdAction } from './model/actions/set-selected-order-id-action';
import { OrdersActionType } from './model/orders-action-type';
import { OrderResetAction } from './model/actions/order-reset-action';
import { SubmitScheduleAppointmentFailureAction } from './model/actions/submit-schedule-appointment-failure-action';
import { SubmitScheduleAppointmentSuccessAction } from './model/actions/submit-schedule-appointment-success-action';
import { SubmitScheduleAppointmentStartAction } from './model/actions/submit-schedule-appointment-start-action';
import { InitializeOrdersFailureAction } from './model/actions/initialize-orders-failure-action';
import { InitializeOrdersStartAction } from './model/actions/initialize-orders-start-action';
import { InitializeOrdersSuccessAction } from './model/actions/initialize-orders-success-action';
import { InitializeBillingAccountsStartAction } from './model/actions/initialize-billing-accounts-start-action';
import { InitializeBillingAccountsSuccessAction } from './model/actions/initialize-billing-accounts-success-action';
import { InitializeBillingAccountsFailureAction } from './model/actions/initialize-billing-accounts-failure-action';
import { LoadCustomerInformationStartAction } from './model/actions/load-customer-information-start-action';
import { LoadCustomerInformationSuccessAction } from './model/actions/load-customer-information-success-action';
import { LoadCustomerInformationFailureAction } from './model/actions/load-customer-information-failure-action';
import { UpdateActivationDateStartAction } from './model/actions/update-activation-date-start-action';
import { UpdateActivationDateSuccessAction } from './model/actions/update-activation-date-success-action';
import { UpdateActivationDateFailureAction } from './model/actions/update-activation-date-failure-action';
import { UpdateShoppingCartStartAction } from './model/actions/update-shopping-cart-start-action';
import { UpdateShoppingCartSuccessAction } from './model/actions/update-shopping-cart-success-action';
import { UpdateShoppingCartFailureAction } from './model/actions/update-shopping-cart-failure-action';

const initializeBillingAccountsActionCreator = () => (dispatch: Dispatch, getState: () => RootState) => {
    const startAction: InitializeBillingAccountsStartAction = {
        type: OrdersActionType.INITIALIZE_BILLING_ACCOUNTS_START,
    };

    dispatch(startAction);

    loadBillingAccountsActionCreator()(dispatch)
        .then((billingAccounts) => {
            billingAccounts.forEach((billingAccount) => loadCustomerInformationActionCreator(billingAccount)(dispatch));

            const queryAccountId: string | null = navigationService.getAccountIdFromQueryString();
            const selectedBillingAccount: BillingAccount | undefined = queryAccountId
                ? billingAccounts.find((acc) => acc.accountId === queryAccountId)
                : billingAccounts.length === 1
                ? billingAccounts[0]
                : undefined;

            if (selectedBillingAccount) {
                setSelectedBillingAccountIdActionCreator(selectedBillingAccount)(dispatch, getState);
                navigationService.goToPath(NavigationService.CHOOSE_SERVICE_LOCATION);
            }

            const successAction: InitializeBillingAccountsSuccessAction = {
                type: OrdersActionType.INITIALIZE_BILLING_ACCOUNTS_SUCCESS,
            };

            dispatch(successAction);
        })
        .catch(() => {
            const failureAction: InitializeBillingAccountsFailureAction = {
                type: OrdersActionType.INITIALIZE_BILLING_ACCOUNTS_FAILURE,
            };

            dispatch(failureAction);
        });
};

const loadBillingAccountsActionCreator =
    () =>
    (dispatch: Dispatch): Promise<BillingAccount[]> => {
        const startAction: LoadBillingAccountsStartAction = {
            type: OrdersActionType.LOAD_BILLING_ACCOUNTS_START,
        };

        dispatch(startAction);

        return userInfoService
            .getUserInfo()
            .then((userInfo) => {
                const billingAccounts: BillingAccount[] = userInfo.billingAccounts
                    .filter((billingAccount) => billingAccount.partnerId === PartnerStaticData.DK_PARTNER_ID)
                    .map(BillingAccountMapper.mapBillingAccount);

                const successAction: LoadBillingAccountsSuccessAction = {
                    type: OrdersActionType.LOAD_BILLING_ACCOUNTS_SUCCESS,
                    billingAccounts,
                };

                dispatch(successAction);
                return billingAccounts;
            })
            .catch((reason) => {
                const failureAction: LoadBillingAccountsFailureAction = {
                    type: OrdersActionType.LOAD_BILLING_ACCOUNTS_FAILURE,
                    reason,
                };

                dispatch(failureAction);
                return Promise.reject();
            });
    };

const loadCustomerInformationActionCreator = (billingAccount: BillingAccount) => (dispatch: Dispatch) => {
    const { partnerId, accountId } = billingAccount;

    const startAction: LoadCustomerInformationStartAction = {
        type: OrdersActionType.LOAD_CUSTOMER_INFORMATION_START,
        billingAccountId: accountId,
    };

    dispatch(startAction);

    CustomerService.getCustomerInformation(partnerId, accountId)
        .then((customerInformation) => {
            const successAction: LoadCustomerInformationSuccessAction = {
                type: OrdersActionType.LOAD_CUSTOMER_INFORMATION_SUCCESS,
                customerInformation,
            };

            dispatch(successAction);
        })
        .catch((reason) => {
            const failureAction: LoadCustomerInformationFailureAction = {
                type: OrdersActionType.LOAD_CUSTOMER_INFORMATION_FAILURE,
                billingAccountId: accountId,
                reason,
            };

            dispatch(failureAction);
        });
};

const setSelectedBillingAccountIdActionCreator =
    (billingAccount: BillingAccount) => (dispatch: Dispatch, getState: () => RootState) => {
        const setSelectedBillingAccountAction: SetSelectedBillingAccountIdAction = {
            type: OrdersActionType.SET_SELECTED_BILLING_ACCOUNT_ID,
            selectedBillingAccountId: billingAccount.accountId,
        };

        dispatch(setSelectedBillingAccountAction);
        if (!billingAccount.initialized) {
            initializeOrdersActionCreator(billingAccount)(dispatch, getState);
        }
    };

const initializeOrdersActionCreator =
    (billingAccount: BillingAccount) => (dispatch: Dispatch, getState: () => RootState) => {
        const { accountId } = billingAccount;

        const startAction: InitializeOrdersStartAction = {
            type: OrdersActionType.INITIALIZE_ORDERS_START,
            billingAccountId: accountId,
        };

        dispatch(startAction);

        loadInflightOrdersActionCreator(billingAccount)(dispatch)
            .then((activeOrders) => {
                const keys: string[] = Object.keys(activeOrders);

                if (keys.length === 1) {
                    setSelectedOrderIdActionCreator(keys[0], accountId)(dispatch, getState);
                }

                if (keys.length === 0) {
                    navigationService.goToPath(NavigationService.NO_INFLIGHT_ORDERS);
                }

                const successAction: InitializeOrdersSuccessAction = {
                    type: OrdersActionType.INITIALIZE_ORDERS_SUCCESS,
                    billingAccountId: accountId,
                };

                dispatch(successAction);
            })
            .catch(() => {
                const successAction: InitializeOrdersFailureAction = {
                    type: OrdersActionType.INITIALIZE_ORDERS_FAILURE,
                    billingAccountId: accountId,
                };

                dispatch(successAction);
            });
    };

const loadInflightOrdersActionCreator =
    (billingAccount: BillingAccount) =>
    (dispatch: Dispatch): Promise<Record<string, ActiveOrder>> => {
        const { accountId, partnerId } = billingAccount;

        const startAction: LoadInflightOrdersStartAction = {
            type: OrdersActionType.LOAD_INFLIGHT_ORDERS_START,
        };

        dispatch(startAction);

        return ShoppingCartService.getActiveOrders(partnerId, accountId)
            .then((activeOrders) => {
                const successAction: LoadInflightOrdersSuccessAction = {
                    type: OrdersActionType.LOAD_INFLIGHT_ORDERS_SUCCESS,
                    activeOrders,
                    billingAccountId: accountId,
                };

                dispatch(successAction);
                return activeOrders;
            })
            .catch((reason) => {
                const failureAction: LoadInflightOrdersFailureAction = {
                    type: OrdersActionType.LOAD_INFLIGHT_ORDERS_FAILURE,
                    reason,
                };

                dispatch(failureAction);
                return Promise.reject(reason);
            });
    };

const setSelectedOrderIdActionCreator =
    (selectedOrderId: string, billingAccountId: string) => (dispatch: Dispatch, getState: () => RootState) => {
        const {
            orders: { billingAccounts },
        } = getState();

        const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(billingAccounts, billingAccountId);

        const setSelectedOrderIdAction: SetSelectedOrderIdAction = {
            type: OrdersActionType.SET_SELECTED_ORDER_ID,
            selectedOrderId,
            billingAccountId,
        };

        dispatch(setSelectedOrderIdAction);

        const order: ActiveOrder = billingAccount.activeOrders[selectedOrderId];

        if (!order.initialized && !order.pendingUpdate) {
            createOrderSummaryActionCreator(billingAccount.partnerId, billingAccountId, selectedOrderId)(dispatch);
        }
        navigationService.goToPath(NavigationService.SUMMARY);
    };

const createOrderSummaryActionCreator =
    (partnerId: string, billingAccountId: string, orderId: string) => (dispatch: Dispatch) => {
        const startAction: CreateOrderSummaryStartAction = {
            type: OrdersActionType.CREATE_ORDER_SUMMARY_START,
            orderId,
            billingAccountId,
        };

        dispatch(startAction);

        ShoppingCartService.createOrderSummary(partnerId, orderId)
            .then((orderSummary) => {
                const successAction: CreateOrderSummarySuccessAction = {
                    type: OrdersActionType.CREATE_ORDER_SUMMARY_SUCCESS,
                    orderId,
                    orderSummary,
                    billingAccountId,
                };

                dispatch(successAction);
            })
            .catch((reason) => {
                const failureAction: CreateOrderSummaryFailureAction = {
                    type: OrdersActionType.CREATE_ORDER_SUMMARY_FAILURE,
                    orderId,
                    reason,
                    billingAccountId,
                };

                dispatch(failureAction);
            });
    };

const initializeScheduleAppointmentActionCreator =
    (availableTimeProvider: AvailableTimeProvider) => (dispatch: Dispatch, getState: () => RootState) => {
        const {
            orders: { selectedBillingAccountId, billingAccounts },
        } = getState();

        if (!selectedBillingAccountId) {
            throw new Error('Billing account must be selected.');
        }

        const { partnerId, accountId, selectedOrderId, activeOrders } = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );

        if (!selectedOrderId) {
            throw new Error('Order must be selected.');
        }

        const cartId: string = OrderUtils.getShoppingCart(activeOrders[selectedOrderId]).id;
        const servicePointId: string = OrderUtils.getServicePointId(activeOrders[selectedOrderId]);

        const startAction: InitializeScheduleAppointmentStartAction = {
            type: OrdersActionType.INITIALIZE_SCHEDULE_APPOINTMENT_START,
            orderId: selectedOrderId,
        };

        dispatch(startAction);

        ShoppingCartService.getAppointment(partnerId, selectedBillingAccountId, servicePointId)
            .then((appointment) =>
                availableTimeProvider
                    .fetchAndMergeAvailableTime(
                        partnerId,
                        cartId,
                        selectedBillingAccountId,
                        servicePointId,
                        (appointment.date ? moment(appointment.date) : moment()).startOf('month'),
                    )
                    .then((availableTime) => ({ appointment, availableTime })),
            )
            .then(({ appointment, availableTime }) => {
                const successAction: InitializeScheduleAppointmentSuccessAction = {
                    type: OrdersActionType.INITIALIZE_SCHEDULE_APPOINTMENT_SUCCESS,
                    appointment,
                    availableTime,
                    orderId: selectedOrderId,
                    billingAccountId: accountId,
                };

                dispatch(successAction);
            })
            .catch((reason) => {
                const failureAction: InitializeScheduleAppointmentFailureAction = {
                    type: OrdersActionType.INITIALIZE_SCHEDULE_APPOINTMENT_FAILURE,
                    reason,
                };

                dispatch(failureAction);
            });
    };

const fetchAvailableTimeActionCreator =
    (day: moment.Moment, availableTimeProvider: AvailableTimeProvider) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const {
            orders: { selectedBillingAccountId, billingAccounts },
        } = getState();

        if (!selectedBillingAccountId) {
            throw new Error('Billing account must be selected.');
        }

        const { partnerId, selectedOrderId, activeOrders } = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );

        if (!selectedOrderId) {
            throw new Error('Order must be selected.');
        }

        const cartId: string = OrderUtils.getShoppingCart(activeOrders[selectedOrderId]).id;
        const servicePointId: string = OrderUtils.getServicePointId(activeOrders[selectedOrderId]);

        const startAction: FetchAvailableTimeStartAction = {
            type: OrdersActionType.FETCH_AVAILABLE_TIME_START,
        };

        dispatch(startAction);

        availableTimeProvider
            .fetchAndMergeAvailableTime(partnerId, cartId, selectedBillingAccountId, servicePointId, day)
            .then((availableTime) => {
                const successAction: FetchAvailableTimeSuccessAction = {
                    type: OrdersActionType.FETCH_AVAILABLE_TIME_SUCCESS,
                    availableTime,
                };

                dispatch(successAction);
            })
            .catch(() => {
                // TODO: think how to show such errors to user
                const successAction: FetchAvailableTimeSuccessAction = {
                    type: OrdersActionType.FETCH_AVAILABLE_TIME_SUCCESS,
                    availableTime: [],
                };

                dispatch(successAction);
            });
    };

const submitScheduleAppointmentActionCreator =
    (appointment: OrderTrackerBackend.Appointment) => (dispatch: Dispatch, getState: () => RootState) => {
        const {
            orders: { selectedBillingAccountId, billingAccounts },
        } = getState();

        if (!selectedBillingAccountId) {
            throw new Error('Billing account must be selected.');
        }

        const { partnerId, selectedOrderId, activeOrders } = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );

        if (!selectedOrderId) {
            throw new Error('Order must be selected.');
        }

        const servicePointId: string = OrderUtils.getServicePointId(activeOrders[selectedOrderId]);

        const startAction: SubmitScheduleAppointmentStartAction = {
            type: OrdersActionType.SUBMIT_SCHEDULE_APPOINTMENT_START,
        };

        dispatch(startAction);

        ShoppingCartService.scheduleAppointment(partnerId, selectedBillingAccountId, servicePointId, appointment)
            .then(() => {
                navigationService.goToPath(NavigationService.SUMMARY);
                openSuccessNotification(i18n.summary.successMessage.updated);
                const successAction: SubmitScheduleAppointmentSuccessAction = {
                    type: OrdersActionType.SUBMIT_SCHEDULE_APPOINTMENT_SUCCESS,
                    orderId: selectedOrderId,
                    billingAccountId: selectedBillingAccountId,
                    appointment,
                };

                dispatch(successAction);
            })
            .catch((reason) => {
                const failureAction: SubmitScheduleAppointmentFailureAction = {
                    type: OrdersActionType.SUBMIT_SCHEDULE_APPOINTMENT_FAILURE,
                    reason,
                };

                dispatch(failureAction);
            });
    };

const updateActivationDateActionCreator = () => (dispatch: Dispatch, getState: () => RootState) => {
    const {
        orders: { selectedBillingAccountId, billingAccounts },
    } = getState();

    if (!selectedBillingAccountId) {
        throw new Error('Billing account must be selected.');
    }

    const selectedBillingAccount = BillingAccountUtils.getBillingAccount(billingAccounts, selectedBillingAccountId);

    const { activeOrders, selectedOrderId } = selectedBillingAccount;

    if (!selectedOrderId) {
        throw new Error('Order must be selected.');
    }

    const partnerId: string = selectedBillingAccount.partnerId;
    const activeOrder: ActiveOrder = activeOrders[selectedOrderId];
    const previousActivationDate: string = OrderUtils.getActivationDate(activeOrder);
    const updateActivationDateFormValues: UpdateActivationDateFormModel = getFormValues<UpdateActivationDateFormModel>(
        FormName.UPDATE_ACTIVATION_DATE,
    )(getState());

    if (updateActivationDateFormValues.activationDate !== previousActivationDate) {
        const startAction: UpdateActivationDateStartAction = {
            type: OrdersActionType.UPDATE_ACTIVATION_DATE_START,
        };

        dispatch(startAction);

        ShoppingCartService.updateActivationDate(
            partnerId,
            selectedOrderId,
            updateActivationDateFormValues.activationDate,
        )
            .then(() => {
                const successAction: UpdateActivationDateSuccessAction = {
                    type: OrdersActionType.UPDATE_ACTIVATION_DATE_SUCCESS,
                };

                dispatch(successAction);
                openSuccessNotification(i18n.summary.successMessage.updated);
                navigationService.goToPath(NavigationService.SUMMARY);
                getShoppingCartActionCreator(selectedBillingAccountId, selectedOrderId)(dispatch, getState);
            })
            .catch((reason) => {
                const failureAction: UpdateActivationDateFailureAction = {
                    type: OrdersActionType.UPDATE_ACTIVATION_DATE_FAILURE,
                    reason,
                };

                dispatch(failureAction);
            });
    } else {
        navigationService.goToPath(NavigationService.SUMMARY);
    }
};

const getShoppingCartActionCreator =
    (billingAccountId: string, orderId: string) => (dispatch: Dispatch, getState: () => RootState) => {
        const {
            orders: { billingAccounts },
        } = getState();

        const selectedBillingAccount = BillingAccountUtils.getBillingAccount(billingAccounts, billingAccountId);

        const partnerId: string = selectedBillingAccount.partnerId;
        const cartId: string = OrderUtils.getShoppingCart(selectedBillingAccount.activeOrders[orderId]).id;

        const startAction: UpdateShoppingCartStartAction = {
            type: OrdersActionType.UPDATE_SHOPPING_CART_START,
            orderId,
            billingAccountId,
        };

        dispatch(startAction);

        ShoppingCartService.getShoppingCart(partnerId, cartId)
            .then((shoppingCart) => {
                const successAction: UpdateShoppingCartSuccessAction = {
                    type: OrdersActionType.UPDATE_SHOPPING_CART_SUCCESS,
                    orderId,
                    billingAccountId,
                    shoppingCart,
                };

                dispatch(successAction);
            })
            .catch((reason) => {
                const failureAction: UpdateShoppingCartFailureAction = {
                    type: OrdersActionType.UPDATE_SHOPPING_CART_FAILURE,
                    orderId,
                    billingAccountId,
                    reason,
                };

                dispatch(failureAction);
            });
    };

const orderResetActionCreator =
    () =>
    (dispatch: Dispatch): void => {
        const orderResetAction: OrderResetAction = {
            type: OrdersActionType.ORDER_RESET,
        };

        dispatch(orderResetAction);
    };

export {
    initializeBillingAccountsActionCreator,
    setSelectedBillingAccountIdActionCreator,
    loadCustomerInformationActionCreator,
    loadInflightOrdersActionCreator,
    createOrderSummaryActionCreator,
    setSelectedOrderIdActionCreator,
    initializeScheduleAppointmentActionCreator,
    fetchAvailableTimeActionCreator,
    submitScheduleAppointmentActionCreator,
    updateActivationDateActionCreator,
    getShoppingCartActionCreator,
    orderResetActionCreator,
};
